// SignalR Provider for managing real-time connection state and updates
// Integrates with AuthProvider to handle connections on sign-in/sign-out

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Platform } from 'react-native';
import { SignalRService, SignalRUpdateData } from '@/services/SignalRService';
import { useAuth } from './AuthProvider';
import { useAppState } from '@/hooks/useAppState';
import { obfuscateToken } from '@/libs/signalr/tokenObfuscation';

interface SignalRContextProps {
  isConnected: boolean;
  isConnecting: boolean;
  isOnline: boolean;
  // Debounced connection problem for the UI banner; null when healthy. Lives
  // here (not in the banner component) so it survives screen navigation.
  connectionBannerStatus: 'offline' | 'connecting' | null;
  connectionError: string | null;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  getConnectionInfo: () => {
    isConnected: boolean;
    transport: string;
    connectionId: string | null;
    state: number | null;
  };
  setMessageId: (id: number | null) => void;
}

// Per-push volatile fields live in their own context so a new message/notification
// only re-renders badge/list consumers, not every connection-state consumer.
interface SignalRUpdatesContextProps {
  messageCount: number;
  notificationCount: number;
  messageId: number | null;
}

const SignalRContext = createContext<SignalRContextProps | undefined>(undefined);
const SignalRUpdatesContext = createContext<SignalRUpdatesContextProps | undefined>(undefined);

interface SignalRProviderProps {
  children: React.ReactNode;
  loggingLevel?: 'none' | 'info' | 'debug';
}

export const SignalRProvider: React.FC<SignalRProviderProps> = ({ 
  children, 
  loggingLevel = 'none'
}) => {
  const { isAuthenticated, currentCommunity, getCredentials } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [connectionBannerStatus, setConnectionBannerStatus] = useState<
    'offline' | 'connecting' | null
  >(null);
  const [messageCount, setMessageCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [isBackgrounded, setIsBackgrounded] = useState(false);
  const [messageId, setMessageId] = useState<number | null>(null);
  // Refs (not state): only read inside the connect/disconnect callbacks, so a
  // debounced attempt shouldn't re-render the provider or churn callback identities.
  const lastConnectAttemptRef = useRef<number>(0);
  const lastDisconnectAttemptRef = useRef<number>(0);

  // Handle SignalR updates from the server
  const handleSignalRUpdate = useCallback((data: SignalRUpdateData) => {
    console.log('[SignalRProvider] Received update', loggingLevel !== 'none' ? data : '');

    // Update message count if provided and not null
    if (data.messageCount !== null && data.messageCount !== undefined) {
      setMessageCount(data.messageCount);
    }

    // Update notification count if provided and not null
    if (data.notificationCount !== null && data.notificationCount !== undefined) {
      setNotificationCount(data.notificationCount);
    }

    // Update messageId if provided
    if (data.messageId !== null && data.messageId !== undefined) {
      setMessageId(data.messageId);
    }

    // TODO: Implement other handlers later
    // - playSound: Could trigger a notification sound
  }, []);

  // Connect to SignalR when authenticated
  const connectSignalR = useCallback(async () => {
    const now = Date.now();
    
    // Check if we are switching communities
    const serviceCommunity = SignalRService.getCurrentCommunity();
    const isCommunitySwitch = serviceCommunity !== currentCommunity;
    
    // Debounce: only allow connection attempts once per 2s
    // Exception: If we are switching communities, we should process immediately
    if (!isCommunitySwitch && now - lastConnectAttemptRef.current < 2000) {
      console.log('[SignalRProvider] Connection attempt debounced - too soon after last attempt');
      return;
    }

    lastConnectAttemptRef.current = now;

    if (!currentCommunity || !isAuthenticated) {
      console.log('[SignalRProvider] Cannot connect - no community or not authenticated');
      return;
    }

    const credentials = getCredentials(currentCommunity);
    if (!credentials) {
      console.log('[SignalRProvider] Cannot connect - no credentials');
      return;
    }

    try {
      setIsConnecting(true);
      setConnectionError(null);

      console.log('[SignalRProvider] Connecting to community:', currentCommunity);

      // Obfuscate the auth token for security (mobile platforms only)
      const obfuscatedToken = credentials?.AuthToken ? obfuscateToken(credentials.AuthToken) : undefined;

      await SignalRService.connect(currentCommunity, obfuscatedToken, handleSignalRUpdate, loggingLevel);

      setIsConnected(true);
      console.log('[SignalRProvider] Connected successfully');
    } catch (error) {
      console.error('[SignalRProvider] Connection failed:', error);
      setConnectionError(error instanceof Error ? error.message : 'Connection failed');
      setIsConnected(false);
    } finally {
      setIsConnecting(false);
    }
  }, [currentCommunity, isAuthenticated, getCredentials, handleSignalRUpdate, loggingLevel]);

  // Disconnect from SignalR
  const disconnectSignalR = useCallback(async () => {
    const now = Date.now();
    
    // Debounce: only allow disconnect attempts once per 2s
    if (now - lastDisconnectAttemptRef.current < 2000) {
      console.log('[SignalRProvider] Disconnect attempt debounced - too soon after last attempt');
      return;
    }

    lastDisconnectAttemptRef.current = now;

    try {
      console.log('[SignalRProvider] Disconnecting...');
      setIsConnecting(false);

      await SignalRService.disconnect();

      setIsConnected(false);
      setMessageCount(0);
      setNotificationCount(0);
      setConnectionError(null);

      console.log('[SignalRProvider] Disconnected successfully');
    } catch (error) {
      console.error('[SignalRProvider] Error during disconnect:', error);
    }
  }, []);

  const handleAppBackground = useCallback(async () => {
    if (Platform.OS === 'web') return;

    console.log('[SignalRProvider] App went to background, disconnecting SignalR');
    setIsBackgrounded(true);

    if (isConnected || isConnecting) {
      await disconnectSignalR();
    }
  }, [isConnected, isConnecting, disconnectSignalR]);

  const handleAppForeground = useCallback(async () => {
    if (Platform.OS === 'web') {
      // On web, the browser may have killed the connection while the tab was
      // hidden. Re-establish it only if the underlying transport is dead.
      if (isAuthenticated && currentCommunity && !SignalRService.isConnected() && !SignalRService.isConnecting()) {
        console.log('[SignalRProvider] Tab became visible, reconnecting SignalR');
        await connectSignalR();
      }
      return;
    }

    console.log('[SignalRProvider] App came to foreground, reconnecting SignalR');
    setIsBackgrounded(false);

    if (isAuthenticated && currentCommunity) {
      await connectSignalR();
    }
  }, [isAuthenticated, currentCommunity, connectSignalR]);

  useAppState({
    onBackground: handleAppBackground,
    onForeground: handleAppForeground,
    debugTag: 'SignalR',
  });

  // Effect to handle authentication changes
  useEffect(() => {
    if (isAuthenticated && currentCommunity && !isBackgrounded) {
      // Check if we need to switch communities
      const serviceCommunity = SignalRService.getCurrentCommunity();
      const isConnectedToWrongCommunity = serviceCommunity !== currentCommunity;

      // User signed in and app is in foreground - connect to SignalR
      if ((!isConnected && !isConnecting) || isConnectedToWrongCommunity) {
        connectSignalR();
      }
    } else {
      // User signed out, no community, or app is backgrounded - disconnect from SignalR
      if (isConnected) {
        disconnectSignalR();
      }
    }
  }, [isAuthenticated, currentCommunity, isBackgrounded]);

  // Effect to monitor SignalR service state
  useEffect(() => {
    const checkConnectionStatus = () => {
      const serviceConnected = SignalRService.isConnected();
      const serviceConnecting = SignalRService.isConnecting();
      const serviceOnline = SignalRService.isOnline();

      if (serviceConnected !== isConnected) {
        setIsConnected(serviceConnected);
      }

      if (serviceConnecting !== isConnecting) {
        setIsConnecting(serviceConnecting);
      }

      if (serviceOnline !== isOnline) {
        setIsOnline(serviceOnline);
      }
    };

    // Check status immediately
    checkConnectionStatus();

    // Set up periodic status check
    const interval = setInterval(checkConnectionStatus, 2000);

    return () => clearInterval(interval);
  }, [isConnected, isConnecting, isOnline]);

  // Debounce connection problems so a brief blip doesn't flash the banner. The
  // resulting status lives in the provider (a singleton), so it persists across
  // screen navigation — the banner shows immediately when a relevant screen
  // mounts instead of re-waiting the grace period each time.
  useEffect(() => {
    // While backgrounded the connection is intentionally torn down; don't treat
    // that as a problem or leave a stale status to flash when we return.
    if (isBackgrounded) {
      setConnectionBannerStatus(null);
      return;
    }

    // Web has no reliable device-level connectivity signal: navigator.onLine
    // (all expo-network gives us on web) is unreliable in both directions, and
    // probing our own API can't tell "user offline" from "server down". So on
    // web we never claim "No internet connection" — we only surface
    // "Connecting..." while the realtime link is actually down. Native has
    // accurate OS connectivity (NWPathMonitor / ConnectivityManager), so it
    // keeps the offline-vs-connecting distinction.
    const problem: 'offline' | 'connecting' | null =
      Platform.OS === 'web'
        ? isConnected
          ? null
          : 'connecting'
        : !isOnline
          ? 'offline'
          : !isConnected
            ? 'connecting'
            : null;

    if (problem === null) {
      setConnectionBannerStatus(null);
      return;
    }

    const timer = setTimeout(() => setConnectionBannerStatus(problem), 3000);
    return () => clearTimeout(timer);
  }, [isConnected, isOnline, isBackgrounded]);

  // Cleanup on unmount
  // useEffect(() => {
  //   return () => {
  //     disconnectSignalR();
  //   };
  // }, [disconnectSignalR]);

  // Get connection info wrapper
  const getConnectionInfo = useCallback(() => {
    return SignalRService.getConnectionInfo();
  }, []);

  const contextValue = useMemo<SignalRContextProps>(
    () => ({
      isConnected,
      isConnecting,
      isOnline,
      connectionBannerStatus,
      connectionError,
      connect: connectSignalR,
      disconnect: disconnectSignalR,
      getConnectionInfo,
      setMessageId,
    }),
    [
      isConnected,
      isConnecting,
      isOnline,
      connectionBannerStatus,
      connectionError,
      connectSignalR,
      disconnectSignalR,
      getConnectionInfo,
    ]
  );

  const updatesValue = useMemo<SignalRUpdatesContextProps>(
    () => ({ messageCount, notificationCount, messageId }),
    [messageCount, notificationCount, messageId]
  );

  return (
    <SignalRContext.Provider value={contextValue}>
      <SignalRUpdatesContext.Provider value={updatesValue}>{children}</SignalRUpdatesContext.Provider>
    </SignalRContext.Provider>
  );
};

export const useSignalR = () => {
  const context = useContext(SignalRContext);
  if (!context) {
    throw new Error('useSignalR must be used within a SignalRProvider');
  }
  return context;
};

export const useSignalRUpdates = () => {
  const context = useContext(SignalRUpdatesContext);
  if (!context) {
    throw new Error('useSignalRUpdates must be used within a SignalRProvider');
  }
  return context;
};
